/**
 * Nexus Self-Development Panel — admin-only, one-click UX.
 *
 * Design principle (2026-07-07 iteration): the admin sees ONE primary
 * button: "Publicar cambios y preparar Deploy". Everything else runs
 * behind the scenes:
 *
 *   1. On panel mount → auto-bootstrap the self-workspace (idempotent).
 *   2. Click primary CTA → prompt for optional commit message → publish.
 *   3. Server auto-detects "no changes" and returns a friendly message.
 *   4. Success → show commit_sha + banner "Ahora presiona Deploy en
 *      el chat de Emergent" (the only step still manual).
 *
 * A collapsible "Ver detalles" reveals the raw status/diff for admins
 * who want to inspect before publishing.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import apiService from '../services/api';

type BootstrapInfo = {
  workspace_id: string;
  workspace_name: string;
  imported_path: string;
  repo_url: string;
  created: boolean;
  already_exists: boolean;
};

type StatusInfo = {
  workspace_id: string;
  branch: string;
  dirty: boolean;
  modified: string[];
  added: string[];
  deleted: string[];
  untracked_count: number;
  diff_stat: string;
  commits_ahead: number;
};

type PublishResult = {
  ok: boolean;
  pushed: boolean;
  repo: string;
  branch: string;
  commit_sha?: string;
  pushed_files: number;
  repo_url?: string;
  next_step: string;
  error?: string;
};

function extractError(e: unknown): string {
  return (
    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
    || String(e)
  );
}

export default function SelfDevPanel() {
  const [ws, setWs] = useState<BootstrapInfo | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [commitMessage, setCommitMessage] = useState(
    'chore(nexus): self-development update',
  );
  const [publishResult, setPublishResult] = useState<PublishResult | null>(
    null,
  );
  const [status, setStatus] = useState<StatusInfo | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Auto-bootstrap on mount. `bootstrap` is idempotent so calling it
  // every time the panel opens is safe and avoids the "Import Nexus"
  // step the admin used to have to press manually.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await apiService.selfBootstrap();
        if (!cancelled) setWs(info);
      } catch (e: unknown) {
        if (!cancelled) setBootstrapError(extractError(e));
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadStatus = useCallback(async () => {
    if (!ws) return;
    setLoadingDetails(true);
    try {
      const r = await apiService.selfStatus(ws.workspace_id);
      setStatus(r);
    } catch (e) {
      toast.error(`No pude leer el estado: ${extractError(e)}`);
    } finally {
      setLoadingDetails(false);
    }
  }, [ws]);

  async function doPublish() {
    if (!ws) return;
    setShowConfirm(false);
    setPublishing(true);
    setPublishResult(null);
    try {
      const r = await apiService.selfPublish(
        ws.workspace_id, commitMessage, status?.branch || 'main',
      );
      setPublishResult(r);
      if (r.ok && r.pushed) {
        toast.success('Cambios publicados en GitHub');
      } else if (r.ok && !r.pushed) {
        toast.info('No había cambios que publicar');
      } else {
        toast.error(`El push reportó fallo: ${r.error || 'desconocido'}`);
      }
      // Refresh status so the CTA reflects the new state.
      await loadStatus();
    } catch (e) {
      toast.error(`Publicación falló: ${extractError(e)}`);
    } finally {
      setPublishing(false);
    }
  }

  if (initializing) {
    return (
      <div className="space-y-2 text-xs text-white/60" data-testid="self-dev-panel">
        Iniciando entorno de auto-desarrollo…
      </div>
    );
  }
  if (bootstrapError) {
    return (
      <div className="space-y-3" data-testid="self-dev-panel">
        <div>
          <h3 className="text-sm font-mono uppercase text-white/70 mb-1">
            Auto-desarrollo de Nexus
          </h3>
          <p className="text-xs text-red-300 border border-red-500/30 bg-red-500/5 rounded p-3">
            No pude iniciar el entorno: {bootstrapError}
          </p>
          <p className="text-xs text-white/50 mt-2">
            Verifica que <span className="font-mono">NEXUS_SELF_REPO_URL</span>{' '}
            esté configurada y que tengas tu Personal Access Token de GitHub
            guardado en Integraciones.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="self-dev-panel">
      <div>
        <h3 className="text-sm font-mono uppercase text-white/70 mb-1">
          Auto-desarrollo de Nexus
        </h3>
        <p className="text-xs text-white/40">
          Un click publica todos los cambios que Nexus haya realizado sobre su
          propio código. Solo administradores.
        </p>
      </div>

      {ws && (
        <div
          className="text-[10px] font-mono text-white/40 border border-white/5 rounded px-2 py-1"
          data-testid="self-workspace-info"
        >
          Workspace <span className="text-white/70">{ws.workspace_name}</span>
          {' · '}Repo <span className="text-white/70">{ws.repo_url}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="self-publish-primary"
          onClick={() => {
            loadStatus();  // refresh diff before showing modal
            setShowConfirm(true);
          }}
          disabled={!ws || publishing}
          className="px-4 py-2 text-xs font-mono uppercase bg-white text-black hover:bg-neutral-200 rounded shadow-[0_0_15px_rgba(255,255,255,0.15)] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {publishing ? 'Publicando…' : 'Publicar cambios y preparar Deploy'}
        </button>

        <button
          data-testid="self-toggle-details"
          onClick={() => {
            const next = !showDetails;
            setShowDetails(next);
            if (next) loadStatus();
          }}
          disabled={!ws}
          className="px-2 py-1 text-[10px] font-mono uppercase bg-transparent border border-white/10 text-white/60 hover:bg-white/5 rounded disabled:opacity-40"
        >
          {showDetails ? 'Ocultar detalles' : 'Ver detalles'}
        </button>
      </div>

      {showDetails && (
        <div
          className="text-xs font-mono text-white/60 border border-white/10 rounded p-3"
          data-testid="self-status-info"
        >
          {loadingDetails && <div>Leyendo estado…</div>}
          {status && (
            <>
              <div className="mb-2">
                Branch <span className="text-white">{status.branch}</span>
                {' — '}
                <span className={status.dirty ? 'text-yellow-300' : 'text-emerald-300'}>
                  {status.dirty ? 'cambios pendientes' : 'sin cambios locales'}
                </span>
                {status.commits_ahead > 0 && (
                  <>
                    {' · '}
                    <span className="text-yellow-300">
                      {status.commits_ahead} commit(s) sin publicar
                    </span>
                  </>
                )}
              </div>
              {status.modified.length > 0 && (
                <div>Modificados: {status.modified.length}</div>
              )}
              {status.added.length > 0 && (
                <div>Añadidos: {status.added.length}</div>
              )}
              {status.deleted.length > 0 && (
                <div>Eliminados: {status.deleted.length}</div>
              )}
              {status.untracked_count > 0 && (
                <div>No trackeados: {status.untracked_count}</div>
              )}
              {status.diff_stat && (
                <pre className="mt-2 text-[10px] text-white/40 whitespace-pre-wrap">
                  {status.diff_stat}
                </pre>
              )}
            </>
          )}
        </div>
      )}

      {showConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          data-testid="self-publish-confirm"
        >
          <div className="max-w-md w-full bg-neutral-900 border border-white/10 rounded p-6 space-y-4">
            <div>
              <h4 className="text-white font-mono text-sm uppercase mb-2">
                Confirmar publicación
              </h4>
              <p className="text-xs text-white/60">
                Nexus publicará todos los cambios pendientes al repo{' '}
                <span className="text-white">{ws?.repo_url}</span>. Después
                del push, sólo faltará que presiones <b>Deploy</b> desde el chat.
              </p>
              {status && !status.dirty && status.commits_ahead === 0 && (
                <p className="text-xs text-emerald-300 mt-2">
                  Nota: actualmente no hay cambios que publicar. Al confirmar,
                  no se producirá ningún commit.
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-white/50 block mb-1">
                Mensaje del commit (opcional)
              </label>
              <input
                data-testid="self-publish-commit-msg"
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white font-mono"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="self-publish-cancel"
                onClick={() => setShowConfirm(false)}
                className="px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded font-mono uppercase text-white/70"
              >
                Cancelar
              </button>
              <button
                data-testid="self-publish-approve"
                onClick={doPublish}
                className="px-3 py-1.5 text-xs bg-white text-black hover:bg-neutral-200 rounded font-mono uppercase"
              >
                Sí, publicar
              </button>
            </div>
          </div>
        </div>
      )}

      {publishResult && (
        <div
          className={`text-xs font-mono border rounded p-3 space-y-1 ${
            publishResult.pushed
              ? 'text-white/80 border-emerald-500/30 bg-emerald-500/5'
              : 'text-white/70 border-white/10 bg-white/[0.02]'
          }`}
          data-testid="self-publish-result"
        >
          <div className={publishResult.pushed ? 'text-emerald-300 uppercase text-[10px]' : 'uppercase text-[10px] text-white/50'}>
            {publishResult.pushed ? 'Publicado' : 'Sin cambios'}
          </div>
          {publishResult.pushed && (
            <>
              <div>Repo: {publishResult.repo}</div>
              {publishResult.commit_sha && (
                <div>Commit: <span className="text-white">{publishResult.commit_sha}</span></div>
              )}
              <div>Archivos: {publishResult.pushed_files}</div>
            </>
          )}
          <div className="mt-2 text-white/60">{publishResult.next_step}</div>
        </div>
      )}
    </div>
  );
}
